import { useEffect, useRef } from 'react';

interface UseClipboardImageProps {
  onImagePaste: (
    imageUrl: string,
    width: number,
    height: number,
    mouseX: number,
    mouseY: number,
  ) => void;
}

/**
 * Hook to handle clipboard paste events for images
 * Tracks last mouse position to paste images at cursor location
 */
export const useClipboardImage = ({ onImagePaste }: UseClipboardImageProps) => {
  const lastMousePos = useRef({
    x: window.innerWidth / 2,
    y: window.innerHeight / 2,
  });

  useEffect(() => {
    let rafId: number | null = null;

    const handleMouseMove = (e: MouseEvent) => {
      if (rafId) return; // Skip if already scheduled

      rafId = requestAnimationFrame(() => {
        lastMousePos.current = { x: e.clientX, y: e.clientY };
        rafId = null;
      });
    };

    const handlePaste = async (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      const mouseX = lastMousePos.current.x;
      const mouseY = lastMousePos.current.y;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];

        if (item.type.indexOf('image') !== -1) {
          e.preventDefault();
          const blob = item.getAsFile();
          // eslint-disable-next-line no-continue
          if (!blob) continue;

          const url = URL.createObjectURL(blob);

          const img = new Image();
          img.onload = () => {
            onImagePaste(url, img.width, img.height, mouseX, mouseY);
          };
          img.src = url;

          break;
        }
      }
    };

    document.addEventListener('mousemove', handleMouseMove, { passive: true });
    document.addEventListener('paste', handlePaste);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('paste', handlePaste);
    };
  }, [onImagePaste]);
};
